import React, {
  Suspense, lazy, useCallback, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { dropRight, includes } from 'lodash/fp';
import { Grid, Spinner } from '../../common/components';
import CancelDialog from './CancelDialog';
import StepGuide from './StepGuide';
import AddAnswerAreaPaper from './AddAnswerAreaPaper';
import AddOptionPaper from './AddOptionPaper';
import AddQuestionPaper from './AddQuestionPaper';
import AddSignaturePaper from './AddSignaturePaper';
import SelectRequiredPaper from './SelectRequiredPaper';
import SelectTypePaper from './SelectTypePaper';

const PDFViewer = lazy(() => import(/* webpackChunkName: 'PDFViewer', webpackPrefetch: true */ './PDFViewer'));

const PDFEditor = React.memo(({
  children, file, questions, signatures, setFieldValue, visible, setVisible, onError,
}) => {
  const [assigned, setAssigned] = useState(false);
  const [step, setStep] = useState(0);
  const [type, setType] = useState(null);
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [optionType, setOptionType] = useState('SELECTION');

  const stepCount = useMemo(() => (type === 'SIGNATURE' ? 2 : 4), [type]);

  const closeDialog = useCallback(() => setDialog(false), []);

  const toggleQuestionVisibility = useCallback(() => setVisible(prevVisible => !prevVisible), [setVisible]);

  const handleClick = useCallback(() => {
    setVisible(true);

    if (assigned) {
      setDialog(true);
    } else {
      setOpen(prevOpen => !prevOpen);
    }
  }, [assigned, setVisible]);

  const handleConfirm = useCallback(() => {
    setDialog(false);

    setOpen(false);

    setAssigned(false);
    if (includes(type, ['SINGLE_SELECTION', 'MULTIPLE_SELECTION', 'DESCRIPTIVE'])) {
      setFieldValue('questions', dropRight(1, questions));
    } else {
      setFieldValue('signatures', dropRight(1, signatures));
    }
  }, [questions, setFieldValue, signatures, type]);

  const handleEnter = useCallback(() => setStep(1), []);

  const handleExited = useCallback(() => {
    setStep(0);
    setType(null);
  }, []);

  const finishStep = useCallback(() => {
    setOpen(false);
    setAssigned(false);
  }, []);

  const selectType = useCallback((event) => {
    setType(event.currentTarget.name);
    setStep(prevStep => prevStep + 1);
  }, []);

  return (
    <>
      <Grid container spacing={2} margin={{ bottom: 24 }}>
        <Grid item xs={1} />
        <Grid item xs={6}>
          <Suspense fallback={<Spinner />}>
            <PDFViewer
              file={file}
              questions={questions}
              signatures={signatures}
              visible={visible}
              toggleQuestionVisibility={toggleQuestionVisibility}
              onError={onError}
            >
              {props => children({
                assigned,
                setAssigned,
                step,
                setStep,
                type,
                setType,
                optionType,
                setOptionType,
                ...props,
              })}
            </PDFViewer>
          </Suspense>
        </Grid>
        <Grid item xs={5}>
          <StepGuide
            open={open}
            step={step}
            stepCount={stepCount}
            onClick={handleClick}
            onEnter={handleEnter}
            onExited={handleExited}
          >
            {type ? (
              {
                SINGLE_SELECTION: {
                  2: (
                    <AddQuestionPaper
                      assigned={assigned}
                      setAssigned={setAssigned}
                      questions={questions}
                      setFieldValue={setFieldValue}
                      setStep={setStep}
                      type={type}
                      setType={setType}
                    />
                  ),
                  3: (
                    <AddOptionPaper
                      questions={questions}
                      setFieldValue={setFieldValue}
                      setStep={setStep}
                      type={type}
                      optionType={optionType}
                      setOptionType={setOptionType}
                    />
                  ),
                  4: <SelectRequiredPaper finishStep={finishStep} questions={questions} setFieldValue={setFieldValue} setStep={setStep} type={type} />,
                },
                MULTIPLE_SELECTION: {
                  2: (
                    <AddQuestionPaper
                      assigned={assigned}
                      setAssigned={setAssigned}
                      questions={questions}
                      setFieldValue={setFieldValue}
                      setStep={setStep}
                      type={type}
                      setType={setType}
                    />
                  ),
                  3: (
                    <AddOptionPaper
                      questions={questions}
                      setFieldValue={setFieldValue}
                      setStep={setStep}
                      type={type}
                      optionType={optionType}
                      setOptionType={setOptionType}
                    />
                  ),
                  4: <SelectRequiredPaper finishStep={finishStep} questions={questions} setFieldValue={setFieldValue} setStep={setStep} type={type} />,
                },
                DESCRIPTIVE: {
                  2: (
                    <AddQuestionPaper
                      assigned={assigned}
                      setAssigned={setAssigned}
                      questions={questions}
                      setFieldValue={setFieldValue}
                      setStep={setStep}
                      type={type}
                      setType={setType}
                    />
                  ),
                  3: <AddAnswerAreaPaper questions={questions} setFieldValue={setFieldValue} setStep={setStep} step={step} type={type} />,
                  4: <SelectRequiredPaper finishStep={finishStep} questions={questions} setFieldValue={setFieldValue} setStep={setStep} type={type} />,
                },
                SIGNATURE: {
                  2: (
                    <AddSignaturePaper
                      assigned={assigned}
                      setAssigned={setAssigned}
                      finishStep={finishStep}
                      setFieldValue={setFieldValue}
                      setStep={setStep}
                      setType={setType}
                      signatures={signatures}
                    />
                  ),
                },
              }[type][step]
            ) : (
              <SelectTypePaper onSelect={selectType} />
            )}
          </StepGuide>
        </Grid>
      </Grid>
      <CancelDialog open={dialog} onClose={closeDialog} onConfirm={handleConfirm} />
    </>
  );
});

PDFEditor.propTypes = {
  children: PropTypes.func,
  file: PropTypes.oneOfType([PropTypes.instanceOf(File), PropTypes.instanceOf(Blob)]).isRequired,
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      number: PropTypes.number.isRequired,
      position: PropTypes.shape({
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
      }),
      required: PropTypes.bool.isRequired,
      size: PropTypes.shape({
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
      }),
      type: PropTypes.oneOf(['SINGLE_SELECTION', 'MULTIPLE_SELECTION', 'DESCRIPTIVE']).isRequired,
    }),
  ),
  signatures: PropTypes.arrayOf(
    PropTypes.shape({
      number: PropTypes.number.isRequired,
      position: PropTypes.shape({
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
      }).isRequired,
    }),
  ),
  setFieldValue: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

PDFEditor.defaultProps = {
  children: () => {},
  questions: null,
  signatures: null,
};

export default PDFEditor;
